import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import Widget from '@jimmyboys/liita-ui/dist/components/Widget'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'
import ActionButton from '@jimmyboys/liita-ui/dist/components/ActionButton'
import Collection from '@jimmyboys/liita-ui/dist/components/Collection'
import useQueryParams from '@jimmyboys/liita-ui/dist/hooks/useQueryParams'
import SegmentedControl from '@jimmyboys/liita-ui/dist/components/SegmentedControl'

import { useFetchList } from 'store'
import usePreference from 'hooks/usePreference'
import UserTaskRow, {
	LoadingUserTaskRow,
} from 'components/cards/UserTaskRow/UserTaskRow'
import UserTaskGroupRow from 'components/cards/UserTaskRow/UserTaskGroupRow'
import LastUpdate from 'components/general/LastUpdate/LastUpdate'

import NoData from 'components/general/NoData/NoData'
import { Task } from 'models'

import styles from './Homepage.module.sass'
import themeStyles from 'assets/styles/theme.module.sass'
import { useLoaded } from 'store'
import Dropdown from '@jimmyboys/liita-ui/dist/components/Dropdown'
import { useRecoilState } from 'recoil'
import { opinionMetadataSelectedUserState } from 'store/atoms'

const { rowHeight, rowMargin } = themeStyles
/*CM 194 */
export const TASKS_ORDER = [
	'ManualIPP',
	'FirstIPP',
	'FollowUpIPP',
	'AppointmentClinicalApproval',
	'MedicalActsClinicalApproval',
	'Triage',
	'TransferClinicalApproval',
	'SurgeryClinicalApproval',
	'RelapseClinicalApproval',
	'NonSurgicalAdmissionRequisition',
	'TriageOpinion',
	'TriageOpinionFollowUp',
	'RelapseOpinion',
	'TransferOpinion',
	'ClinicalFollowUpOpinion',
	'CausalLinkOpinion',
]

const Homepage = () => {
	const { i18n } = useGlobal()
	const [expandedGroups, setExpandedGroups] = usePreference(
		'expandedGroups',
		'[]',
		JSON.parse
	)
	const location = useLocation()
	const [queryParams, , setQueryParams] = useQueryParams(location)
	const [loaded, setContentsReady] = useLoaded()

	const [opinionMetadataSelectedUser, setOpinionMetadataSelectedUser] =
		useRecoilState(opinionMetadataSelectedUserState)

	const {
		state,
		contents: [createdTasks, postponedTasks, tasksStats],
		timestamps: [dataTimestamp],
		reload,
	} = useFetchList(
		[
			'/tasks/me?state=Created',
			'/tasks/me?state=Postponed',
			'/tasks/stats',
		],
		[Task.fromJSON, Task.fromJSON],
		undefined,
		undefined,
		undefined,
		loaded
	)

	const tabs = [
		{
			id: 'created',
			text: i18n`Created`,
		},
		{
			id: 'postponed',
			text: i18n`Postponed tasks`,
		},
	]

	const tasksData =
		queryParams?.tasks === 'created' || !queryParams?.tasks
			? createdTasks || []
			: postponedTasks || []

	useEffect(() => {
		if (state === 'hasData') {
			setContentsReady(true)
		}
	}, [state, setContentsReady])

	const isLoading = state === 'loading'
	let tasksList = Array.isArray(tasksData) ? tasksData : []
	tasksList.sort(
		(a, b) =>
			a.priority.id - b.priority.id ||
			a.dueDate.timestamp - b.dueDate.timestamp
	)
	if (queryParams?.company) {
		tasksList = tasksList.filter(
			({ insuranceCompany }) =>
				insuranceCompany?.shortName === queryParams?.company
		)
	}

	const labels = {
		'very-low': i18n`Very Low`,
		low: i18n`Low`,
		medium: i18n`Medium`,
		high: i18n`High`,
		'very-high': i18n`Very High`,
	}

	const insuranceCompaniesFilters = Array.isArray(
		tasksStats?.perInsuranceCount
	)
		? [
				{
					value: '',
					text: i18n`Any`,
				},
				...tasksStats?.perInsuranceCount.map(
					({ insuranceCompanyId }) => {
						return {
							value: insuranceCompanyId,
							text: insuranceCompanyId,
						}
					}
				),
		  ]
		: []

	const selectedCompany = queryParams.company
		? insuranceCompaniesFilters.find(
				({ value }) =>
					queryParams.company.toLocaleLowerCase() ===
					value.toLocaleLowerCase()
		  )
		: null

	const groupedTasks = tasksList.reduce((grouped, task) => {
		const type = task.subType || task.type
		if (!grouped[type]) {
			grouped[type] = {
				isGroup: true,
				id: type,
				type: type,
				icon: task.icon,
				title: task.name,
				dueDate: null,
				tasks: [],
				requiringUsersList: new Set(),
				$cardName: 'UserTaskGroupRow',
			}
		}
		if (
			!grouped[type].dueDate ||
			grouped[type].dueDate.timestamp > task.dueDate.timestamp
		) {
			grouped[type].dueDate = task.dueDate
		}
		grouped[type].tasks.push({
			...task,
			$cardName: 'UserTaskRow',
			$labels: labels,
		})

		grouped[type].subtitle =
			grouped[type].tasks.length === 1
				? i18n`${grouped[type].tasks.length} task`
				: i18n`${grouped[type].tasks.length} tasks`
		return grouped
	}, {})

	if (groupedTasks['ManagementOpinion']) {
		const requiringUsers = groupedTasks['ManagementOpinion'].tasks.reduce(
			(acc, { requiringUser }) => {
				const isUserAdded = acc.find(
					(user) => user.value === requiringUser.id
				)

				if (!isUserAdded) {
					acc.push({
						value: requiringUser.id,
						text: requiringUser.name,
					})
				}

				return acc
			},
			[{ value: '', text: i18n`All` }]
		)

		groupedTasks['ManagementOpinion'].requiringUsers = requiringUsers
		groupedTasks['ManagementOpinion'].$onChange = (e) => {
			setOpinionMetadataSelectedUser(e.target.value)
		}
		groupedTasks['ManagementOpinion'].$selectedUser =
			opinionMetadataSelectedUser

		const allManagementOpinionTasks =
			groupedTasks['ManagementOpinion'].tasks

		if (opinionMetadataSelectedUser) {
			groupedTasks['ManagementOpinion'].tasks = groupedTasks[
				'ManagementOpinion'
			].tasks.filter(
				(task) => task.requiringUser?.id === opinionMetadataSelectedUser
			)
		} else {
			groupedTasks['ManagementOpinion'].tasks = allManagementOpinionTasks
		}
	}

	const baseRowSize = parseFloat(rowHeight) + parseFloat(rowMargin)
	const itemsHeights = []
	const data = []
	const groups = Object.values(groupedTasks)
	groups.sort((a, b) => {
		const aIndex = TASKS_ORDER.findIndex((elem) => elem === a.id)
		const bIndex = TASKS_ORDER.findIndex((elem) => elem === b.id)
		return (
			(aIndex < 0 ? Number.MAX_SAFE_INTEGER : aIndex) -
			(bIndex < 0 ? Number.MAX_SAFE_INTEGER : bIndex)
		)
	})

	groups.forEach((group) => {
		const expanded = expandedGroups.includes(group.id)
		const numberOfItems = Math.min(3, group.tasks.length)
		const multiple = numberOfItems > 2
		const couple = numberOfItems === 2
		data.push({
			...group,
			multiple,
			couple,
			expanded,
		})

		itemsHeights.push(
			baseRowSize +
				(expanded ? 0 : (couple ? 14 : 0) + (multiple ? 14 + 12 : 0))
		)
		if (expanded) {
			group.tasks.forEach((task, index) => {
				const lastChild = index === group.tasks.length - 1
				data.push(task)
				itemsHeights.push(baseRowSize + (lastChild ? 26 : 0))
			})
		}
	})

	const collectionItems = isLoading
		? new Array(20).fill({ isDummy: true, $cardName: 'LoadingUserTaskRow' })
		: data

	const collectionCards = {
		UserTaskRow,
		UserTaskGroupRow,
		LoadingUserTaskRow,
	}

	return (
		<>
			<Widget paddings={['small', 'normal', '']} applyMaxWidth>
				<HStack
					className={styles['header-options']}
					align='center'
					justify='space-between'
					wrap='wrap'
					gap='xsmall'
					flex={[
						[1, 1],
						[0, 0],
					]}
				>
					<HStack
						align='center'
						justify='space-between'
						wrap='nowrap'
						gap='xsmall'
					>
						<HStack gap='xsmall'>
							<div>
								<ActionButton
									icon='reload'
									className={styles['header-button']}
									onClick={() => {
										!isLoading && reload()
									}}
								/>
							</div>
							<VStack>
								<small>
									<strong>{i18n`Tasks`}</strong>
								</small>
								<small className='ellipsis'>
									{i18n`Last Updates`}:{' '}
									{<LastUpdate value={dataTimestamp} />}
								</small>
							</VStack>
						</HStack>
						<SegmentedControl
							selectedTab={queryParams?.tasks}
							onSelect={(tab) => {
								setQueryParams({
									tasks: tab?.id,
								})
							}}
							tabs={tabs}
						/>
					</HStack>
					<div>
						{insuranceCompaniesFilters.length ? (
							<Dropdown
								className={styles['options-entry']}
								name='company'
								placeholder={i18n`Ins. Company`}
								value={
									selectedCompany
										? selectedCompany.value
										: insuranceCompaniesFilters[0].value
								}
								options={insuranceCompaniesFilters}
								onChange={(e) => {
									setQueryParams({
										company: e.target.value,
									})
								}}
							/>
						) : null}
					</div>
				</HStack>
			</Widget>
			<Widget paddings={['normal', 'normal', 'normal']} applyMaxWidth>
				{!isLoading && !(collectionItems || []).length ? (
					<NoData
						applyMaxWidth
						paddings={['normal', 'normal']}
						message={i18n`The are no tasks assigned to you right now.`}
					/>
				) : null}
				<Collection
					items={collectionItems}
					cards={collectionCards}
					renderAhead={20}
					itemsHeight={isLoading ? baseRowSize : undefined}
					itemsHeights={!isLoading ? itemsHeights : undefined}
					onClick={(e) => {
						const hasData = Object.keys(e.data).length
						const target = e.target.closest('[data-dsindex]')
						if (!isLoading && hasData && target) {
							const itemIndex = parseInt(
								target.dataset?.dsindex,
								10
							)
							const item = collectionItems[itemIndex]

							if (item?.isGroup) {
								setExpandedGroups((prev) => {
									const next = [...prev]
									const groupIndex = next.findIndex(
										(id) => item.id === id
									)
									if (groupIndex >= 0) {
										next.splice(groupIndex, 1)
									} else {
										next.push(item.id)
									}
									return JSON.stringify(next)
								})
							}
						}
					}}
				/>
			</Widget>
		</>
	)
}

export default Homepage
